// 1. React and core hooks
import { useState } from 'react';

// 2. Internal components
import { CopyableTextField } from './CopyableTextField';
import { TabBar } from './TabBar';
import { Tabs, TabsContent } from '~/components/ui/tabs';

export interface Template {
  id: string;
  label: string;
  language?: string;
}

export interface TemplateCodeBlockProps<T extends Template> {
  /** Available templates */
  templates: T[];
  /** Generate code string from selected template */
  generateCode: (template: T) => string;
  /** Accessible name for the template tab list */
  legend: string;
  /** Initial template ID (default: first template) */
  defaultTemplateId?: string;
  /** Minimum rows for code display */
  minRows?: number;
}

/**
 * Generic template selection component with code preview.
 * Combines a row of template tabs with a copyable code block. The tab row
 * scrolls horizontally when the labels outgrow the container.
 *
 * @template T - The template type (must extend Template interface)
 */
export function TemplateCodeBlock<T extends Template>({
  templates,
  generateCode,
  legend,
  defaultTemplateId,
  minRows = 15,
}: TemplateCodeBlockProps<T>) {
  const [selectedId, setSelectedId] = useState(defaultTemplateId || templates[0]?.id);

  const selectedTemplate = templates.find((t) => t.id === selectedId) || templates[0];

  const handleChange = (value: string) => {
    setSelectedId(value);
  };

  if (!selectedTemplate) return null;

  return (
    <Tabs value={selectedId} onValueChange={(value: string) => handleChange(value)}>
      <TabBar
        aria-label={legend}
        items={templates.map((template) => ({ value: template.id, label: template.label }))}
      />
      <TabsContent value={selectedTemplate.id}>
        <CopyableTextField
          value={generateCode(selectedTemplate)}
          language={selectedTemplate.language}
          multiline
          minRows={minRows}
          hideUnderline
          hideHelperText
        />
      </TabsContent>
    </Tabs>
  );
}
